import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import MarketingNav from "@/components/layout/MarketingNav";
import PartnerAuthBrandPanel from "@/components/partner/PartnerAuthBrandPanel";
import GoogleSignInButton from "@/components/partner/GoogleSignInButton";

const PartnerLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        toast.success("Welcome back.");
        navigate("/partners/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle({ intent: "login" });
    } finally {
      setGoogleLoading(false);
    }
  };

  const busy = loading || googleLoading;

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-950">
      <MarketingNav />

      <div className="flex-1 flex flex-col lg:flex-row">
        <PartnerAuthBrandPanel
          title="Reach high school students on Skutopia Discover."
          subtitle="Publish scholarships, programs, and placements to learners in the Skutopia app."
        />

        <div className="flex-1 flex items-center justify-center p-6 sm:p-12 lg:p-16 bg-[#f8faf9] dark:bg-gray-950">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full max-w-md"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Sign in to your workspace
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Access your organization's mission control.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8 space-y-5">
              <GoogleSignInButton
                onClick={handleGoogleSignIn}
                disabled={busy}
              />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-gray-900 px-3 text-gray-400">
                    or sign in with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Work email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                    placeholder="you@institution.com"
                    required
                    autoComplete="email"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <Link to="/partners/forgot-password" className="text-xs text-skutopia-600 hover:text-skutopia-700 font-medium">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-gray-300 text-skutopia-600 focus:ring-skutopia-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Remember me on this device</span>
                </label>

                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-skutopia-600 hover:bg-skutopia-700 text-white py-3 rounded-xl font-medium"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </div>

            <div className="mt-8 text-center text-sm">
              <p className="text-gray-500 dark:text-gray-400">
                New organization?{" "}
                <Link to="/partners/register" className="text-skutopia-600 hover:text-skutopia-700 font-medium">
                  Register your organization
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
